import { useEffect, useState } from "react";
import {
  createStyles,
  Header,
  Container,
  Group,
  Burger,
  rem,
  Transition,
  Paper,
  ActionIcon,
  Tooltip,
  Button,
  em,
  useMantineTheme,
  Collapse,
  Box,
  Divider,
} from "@mantine/core";
import { useDisclosure, useMediaQuery } from "@mantine/hooks";
import BenchTitle from "../../../assets/Bench_Logo.png";
import { Link, To } from "react-router-dom";
import {
  IconLogout,
  IconSettings,
  IconUserCircle,
  IconChevronDown,
  IconDots,
  IconPlus,
  IconMaximize,
  IconMaximizeOff,
  IconCircleCheck,
  IconCircleOff,
  IconClock,
  IconMinusVertical,
  IconMessageCircle,
  IconLayoutDashboard,
  IconUsers,
  IconBuildingSkyscraper,
  IconShoppingCart,
  IconMessageReport,
  IconChartBar,
  IconReceipt,
  IconFileAnalytics,
  IconWallet,
  IconBrandTelegram,
  IconCirclePlus,
} from "@tabler/icons-react";
import { Avatar, UnstyledButton, Text, Menu } from "@mantine/core";
import { useNavigate } from "react-router-dom";
import { useLocation } from "react-router-dom";
import { getAuth, signOut } from "firebase/auth";
import {
  onSnapshot,
  doc,
  updateDoc,
  serverTimestamp,
  getDoc,
} from "firebase/firestore";
import { auth, db } from "../../../firebase/FirebaseCredential";
import SimpleRemoteCheckFeature from "../../RemoteCheckFeatureComponent/SimpleRemoteCheckFeature";
import { NavbarList } from "../NavbarList";
import { RootState } from "../../../store/store";
import { useAppDispatch, useAppSelector } from "../../../hooks/hooks";
import { logout } from "../../../features/authSlice";
import NotificationReceiver from "../../UIComponent/NotificationReceiverComponent/NotificationReceiver";
import { useChatContext } from "../../ChatComponent/context/ChatContext";
import ProfileDrawer from "../ProfileDrawer";
import { useUserContext } from "../../../contextapi/UserContext";
import { ROUTES } from "../../../routes/routesPaths";
import { point } from "leaflet";
import BenchLogo from "./BenchLogo";

// Responsive breakpoints
const HEADER_HEIGHT = rem(110);
const HEADER_HEIGHT_MOBILE = rem(70);
const HEIGHT_ADJUSTMENT_PERCENTAGE = 50;

const useStyles = createStyles((theme) => ({
  header: {
    zIndex: 1,
    backgroundColor: theme.white,
    borderBottom: `1px solid ${theme.colors.gray[2]}`,
    transition: "height 200ms ease",
  },

  inner: {
    height: `calc(${HEADER_HEIGHT} * ${HEIGHT_ADJUSTMENT_PERCENTAGE / 100})`,
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",

    [theme.fn.smallerThan("sm")]: {
      height: rem(60),
    },
  },

  logoContainer: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing.xs,
  },

  dropdown: {
    position: "absolute",
    top: HEADER_HEIGHT_MOBILE,
    left: 0,
    right: 0,
    zIndex: 1,
    borderTopRightRadius: 0,
    borderTopLeftRadius: 0,
    borderTopWidth: 0,
    overflow: "hidden",
    boxShadow: theme.shadows.md,
    backgroundColor: theme.white,
    padding: theme.spacing.md,
    maxHeight: "80vh",
    overflowY: "auto",

    [theme.fn.largerThan(769)]: {
      display: "none",
    },
  },

  burger: {
    [theme.fn.largerThan(769)]: {
      display: "none",
    },
  },

  links: {
    paddingTop: theme.spacing.sm,
    height: HEADER_HEIGHT,
    display: "flex",
    flexDirection: "column",
    justifyContent: "space-between",

    [theme.fn.smallerThan("sm")]: {
      display: "none",
    },
  },

  mainLinks: {
    marginRight: `calc(${theme.spacing.sm} * -1)`,
  },

  mainLink: {
    textTransform: "uppercase",
    fontSize: rem(13),
    color:
      theme.colorScheme === "dark"
        ? theme.colors.dark[1]
        : theme.colors.gray[6],
    padding: `${rem(6)} ${theme.spacing.sm}`,
    fontWeight: 700,
    borderBottom: `${rem(2)} solid transparent`,
    transition: "border-color 100ms ease, color 100ms ease",
    textDecoration: "none",

    "&:hover": {
      color: theme.colorScheme === "dark" ? theme.white : theme.black,
      textDecoration: "none",
    },

    [theme.fn.smallerThan("sm")]: {
      borderBottom: "none",
      padding: `${rem(10)} ${rem(12)}`,
      fontSize: rem(16),
      fontWeight: 500,
      display: "flex",
      width: "100%",
      borderRadius: theme.radius.sm,

      "&:hover": {
        backgroundColor: theme.colors.gray[0],
      },
    },
  },

  childLink: {
    textTransform: "uppercase",
    fontSize: rem(13),
    color:
      theme.colorScheme === "dark"
        ? theme.colors.dark[1]
        : theme.colors.gray[6],
    padding: `${rem(6)} ${theme.spacing.sm}`,
    fontWeight: 700,
    borderBottom: `${rem(2)} solid transparent`,
    transition: "border-color 100ms ease, color 100ms ease",
    textDecoration: "none",

    "&:hover": {
      color: theme.colorScheme === "dark" ? theme.white : theme.black,
      textDecoration: "none",
    },

    [theme.fn.smallerThan("sm")]: {
      padding: `${rem(8)} ${rem(12)}`,
      marginLeft: rem(8),
      fontSize: rem(14),
    },
  },

  mainLinkActive: {
    color: theme.colorScheme === "dark" ? theme.white : theme.black,
    borderBottomColor:
      theme.colors[theme.primaryColor][theme.colorScheme === "dark" ? 5 : 6],

    [theme.fn.smallerThan("sm")]: {
      backgroundColor: theme.fn.lighten(
        theme.fn.variant({ variant: "filled", color: theme.primaryColor })
          .background!,
        0.9,
      ),
      color: theme.colors[theme.primaryColor][6],
      borderBottom: "none",
    },
  },

  user: {
    color: theme.white,
    padding: `${theme.spacing.xs} ${theme.spacing.sm}`,
    borderRadius: theme.radius.sm,
    transition: "background-color 100ms ease",

    "&:hover": {
      backgroundColor: theme.fn.lighten(
        theme.fn.variant({ variant: "filled", color: theme.primaryColor })
          .background!,
        0.1,
      ),
    },
  },

  userActive: {
    backgroundColor: theme.fn.lighten(
      theme.fn.variant({ variant: "filled", color: theme.primaryColor })
        .background!,
      0.1,
    ),
  },

  nametext: {
    [theme.fn.smallerThan("xs")]: {
      display: "none",
    },
  },

  rightSection: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing.sm,

    [theme.fn.smallerThan("xs")]: {
      gap: theme.spacing.xs,
    },
  },

  mobileMenu: {
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing.sm,
  },

  mobileMenuSection: {
    marginBottom: theme.spacing.md,
  },

  upgradeButton: {
    [theme.fn.smallerThan("xs")]: {
      display: "none",
    },
  },

  mobileUpgradeButton: {
    display: "none",
    width: "100%",
    marginTop: theme.spacing.md,

    [theme.fn.smallerThan("xs")]: {
      display: "block",
    },
  },

  mobileNavItem: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    padding: `${rem(10)} ${rem(12)}`,
    borderRadius: theme.radius.sm,
    fontWeight: 500,
    fontSize: rem(16),
    color: theme.colors.gray[7],
    cursor: "pointer",

    "&:hover": {
      backgroundColor: theme.colors.gray[0],
    },
  },

  mobileNavItemActive: {
    backgroundColor: theme.fn.lighten(
      theme.fn.variant({ variant: "filled", color: theme.primaryColor })
        .background!,
      0.9,
    ),
    color: theme.colors[theme.primaryColor][6],
  },

  mobileSubLink: {
    display: "block",
    padding: `${rem(8)} ${rem(12)} ${rem(8)} ${rem(24)}`,
    borderRadius: theme.radius.sm,
    fontSize: rem(14),
    color: theme.colors.gray[7],
    textDecoration: "none",

    "&:hover": {
      backgroundColor: theme.colors.gray[0],
    },
  },

  mobileSubLinkActive: {
    backgroundColor: theme.fn.lighten(
      theme.fn.variant({ variant: "filled", color: theme.primaryColor })
        .background!,
      0.9,
    ),
    color: theme.colors[theme.primaryColor][6],
  },

  dropdownControl: {
    width: "100%",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
  },
}));

interface LinkProps {
  label: string;
  link: string;
  more?: boolean;
}

interface User {
  first_name: string;
  last_name: string;
  name: string;
  email: string;
  image: string;
}

interface DoubleHeaderProps {
  mainLinks: LinkProps[];
  user: User;
}

const quickAddLinks = [
  { label: "Staff", path: "/addnewstaff", resource: "staff" },
  { label: "Package", path: "/packagecreate", resource: "packages" },
  { label: "Branch", path: "/addbranchpage", resource: "branches" },
  { label: "Member", path: "/addmemberpage", resource: "members" },
  { label: "Routine", path: "/routineplancreate", resource: "routine" },
  { label: "Expense", path: "/addtransaction", resource: "expenses" },
  { label: "Enquiry", path: "/enquiry-add", resource: "enquiry" },
  {
    label: "Payment",
    path: "/addpaymentreceived",
    resource: "paymentreceived",
  },
  {
    label: "Purchase Order",
    path: ROUTES.PURCHASE_ORDER_PAGE,
    resource: "inventory",
  },
  {
    label: "Import Member",
    path: ROUTES.MEMBER_IMPORT_PAGE,
    resource: "memberimport",
  },
];
// const statusOptions = [
//   { label: "Active", value: "active", icon: IconCircleCheck, color: "green" },
//   { label: "Inactive", value: "inactive", icon: IconCircleOff, color: "gray" },
//   { label: "Busy", value: "busy", icon: IconClock, color: "orange" },
//   { label: "DND", value: "dnd", icon: IconMinusVertical, color: "red" },
// ];
const MultiLayerHeader = ({ mainLinks, user }: DoubleHeaderProps) => {
  const companyId = useAppSelector(
    (state: RootState) => state.session?.company_id,
  );
  const roleId = useAppSelector((state: RootState) => state.session.role_type);
  const { snapshot, loading: subscriptionLoading } = useAppSelector(
    (state: RootState) => state.subscriptionData,
  );
  const { roleDetails } = useUserContext();
  const [opened, { toggle, close }] = useDisclosure(false);
  const { classes, cx } = useStyles();
  const [userMenuOpened, setUserMenuOpened] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  // const theme = useMantineTheme();

  // Responsive breakpoints
  const isMobile = useMediaQuery(`(max-width: ${em(768)})`);
  const isSmallMobile = useMediaQuery(`(max-width: ${em(480)})`);

  const [active, setActive] = useState(location.pathname);
  // const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [attendanceEnabled, setAttendanceEnabled] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [openedDropdowns, setOpenedDropdowns] = useState<string[]>([]);
  const [status, setStatus] = useState("active"); // Default status
  const dispatch = useAppDispatch();
  // const { state, actions, setChatDrawer, chatDrawer } = useChatContext();
  const [openedProfile, { open: openProfile, close: closeProfile }] =
    useDisclosure(false);

  useEffect(() => {
    setActive(location.pathname);
  }, [location.pathname]);

  const hasChat = mainLinks.some((l) => l.label.toLowerCase() === "chat");
  const chatLink =
    mainLinks.find((l) => l.label.toLowerCase() === "chat")?.link ||
    ROUTES.CHAT;

  const filteredLinks = mainLinks.filter(
    (link) => link.label.toLowerCase() !== "chat",
  );

  const iconMap: Record<string, any> = {
    DASHBOARD: IconLayoutDashboard,
    PEOPLE: IconUsers,
    FACILITIES: IconBuildingSkyscraper,
    STORE: IconShoppingCart,
    ENQUIRY: IconMessageReport,
    SALES: IconChartBar,
    EXPENSES: IconReceipt,
    REPORT: IconFileAnalytics,
    PAYROLL: IconWallet,
    COMMUNICATION: IconMessageReport,
  };

  const getIcon = (label: string) => {
    const upperLabel = label.toUpperCase();
    const IconComponent = iconMap[upperLabel];
    if (IconComponent) {
      return <IconComponent size={18} stroke={1.5} />;
    }
    return null;
  };

  useEffect(() => {
    if (companyId) {
      fetchLiveData();
    }
  }, [companyId]);
  useEffect(() => {
    if (!auth.currentUser || !companyId) return;

    const benchprofileDocRef = doc(
      db,
      "benchprofile",
      auth.currentUser.uid,
      "status",
      companyId,
    );

    const unsubscribe = onSnapshot(
      benchprofileDocRef,
      (docSnap) => {
        if (docSnap.exists()) {
          const data = docSnap.data();
          setStatus(data?.activity_status);
          // console.log("Status data:", data);
          // You can now set this to state if needed
        } else {
          console.log("No status document found");
        }
      },
      (error) => {
        console.error("Error listening to status document:", error);
      },
    );

    // Cleanup on unmount
    return () => unsubscribe();
  }, [companyId]);
  const fetchLiveData = (): void => {
    try {
      if (!companyId) {
        console.error("Company ID is null or undefined.");
        return;
      }
      const docRef = doc(
        db,
        "companyprofile",
        companyId,
        "companysettings",
        "attendancesettings",
      );

      onSnapshot(
        docRef,
        (docSnap) => {
          if (docSnap.exists()) {
            const temp = docSnap.data();
            setAttendanceEnabled(
              (temp?.staff_attendance_enabled &&
                temp?.staff_available_options?.includes("manual")) ??
                false,
            );
          } else {
            console.log("Document does not exist.");
          }
        },
        (error: any) => {
          console.error("Error fetching attendance settings:", error);
        },
      );
    } catch (error) {
      console.error("Error setting up live data listener:", error);
    }
  };

  // Handle toggling mobile dropdowns
  const toggleMobileDropdown = (label: string) => {
    setOpenedDropdowns((current) => {
      if (current.includes(label)) {
        return current.filter((item) => item !== label);
      } else {
        return [...current, label];
      }
    });
  };
  const handleSettingClick = () => {
    navigate(ROUTES.SETTING_OVERVIEW);
  };
  // Handle navigation item selection in mobile view
  const handleNavItemClick = (path: string) => {
    setActive(path);
    close(); // Close mobile menu after selection
  };

  // Render mobile menu items separately with expandable dropdowns
  const renderMobileLinks = () => {
    return filteredLinks.map((item: any, i) => {
      // Check if the item has a dropdown
      if (item.dropdown) {
        const isDropdownActive = item.dropdown.some(
          (subItem: any) => subItem.link === active,
        );
        const isOpen = openedDropdowns.includes(item.label);

        if (item.more) {
          return (
            <div key={i}>
              <Box
                className={cx(classes.mobileNavItem, {
                  [classes.mobileNavItemActive]: isDropdownActive,
                })}
                onClick={() => toggleMobileDropdown(item.label)}
              >
                <div className={classes.dropdownControl}>
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "10px",
                    }}
                  >
                    <IconDots size={18} stroke={1.5} />
                    <span>More</span>
                  </div>
                  <IconChevronDown
                    size={16}
                    style={{
                      transform: isOpen ? "rotate(180deg)" : "none",
                      transition: "transform 200ms ease",
                    }}
                  />
                </div>
              </Box>
              <Collapse in={isOpen}>
                <div>
                  {item.dropdown.map((subItem: any) => (
                    <Link
                      key={subItem.link}
                      to={subItem.link}
                      className={cx(classes.mobileSubLink, {
                        [classes.mobileSubLinkActive]: subItem.link === active,
                      })}
                      onClick={() => handleNavItemClick(subItem.link)}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "10px",
                      }}
                    >
                      {getIcon(subItem.label)}
                      {subItem.label}
                    </Link>
                  ))}
                </div>
              </Collapse>
            </div>
          );
        }

        return (
          <div key={item.label}>
            <Box
              className={cx(classes.mobileNavItem, {
                [classes.mobileNavItemActive]: isDropdownActive,
              })}
              onClick={() => toggleMobileDropdown(item.label)}
            >
              <div className={classes.dropdownControl}>
                <div
                  style={{ display: "flex", alignItems: "center", gap: "10px" }}
                >
                  {getIcon(item.label)}
                  <span>{item.label}</span>
                </div>
                <IconChevronDown
                  size={16}
                  style={{
                    transform: isOpen ? "rotate(180deg)" : "none",
                    transition: "transform 200ms ease",
                  }}
                />
              </div>
            </Box>
            <Collapse in={isOpen}>
              <div>
                {item.dropdown.map((subItem: any) => (
                  <Link
                    key={subItem.link}
                    to={subItem.link}
                    className={cx(classes.mobileSubLink, {
                      [classes.mobileSubLinkActive]: subItem.link === active,
                    })}
                    onClick={() => handleNavItemClick(subItem.link)}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "10px",
                    }}
                  >
                    {getIcon(subItem.label)}
                    {subItem.label}
                  </Link>
                ))}
              </div>
            </Collapse>
          </div>
        );
      }

      // For regular links (non-dropdown)
      return (
        <Link
          to={item.link}
          key={item.label}
          className={cx(classes.mobileNavItem, {
            [classes.mobileNavItemActive]: item.link === active,
          })}
          onClick={() => handleNavItemClick(item.link)}
          style={{ justifyContent: "flex-start", gap: "10px" }}
        >
          {getIcon(item.label)}
          {item.label}
        </Link>
      );
    });
  };
  // async function handleStatusChange(status: string) {
  //   setStatus(status);
  //   const benchprofileDocRef = doc(
  //     db,
  //     "benchprofile",
  //     auth.currentUser?.uid || "",
  //     "status",
  //     companyId
  //   );
  //   await updateDoc(benchprofileDocRef, {
  //     activity_status: status,
  //     updated_at: serverTimestamp(),
  //   })
  //     .then(() => {
  //       console.log(`Status updated to ${status}`);
  //     })
  //     .catch((error) => {
  //       console.error("Error updating status:", error);
  //     });
  // }

  const handleProfile = () => {
    // navigate("/profile");
    openProfile();
    close();
  };

  const handleSettingOverviewClick = () => {
    navigate("/settingoverview");
    close();
  };

  const handleLogout = async () => {
    const auth = getAuth();
    const user = auth.currentUser;

    if (!user || !companyId) return;

    try {
      const statusDocRef = doc(
        db,
        "benchprofile",
        user.uid,
        "status",
        companyId,
      );
      const statusDocSnap = await getDoc(statusDocRef);
      if (statusDocSnap.exists()) {
        // Update status to inactive
        await updateDoc(statusDocRef, {
          activity_status: "inactive", // or online: false
          updated_at: new Date(),
        });
      }

      // Then sign out
      await signOut(auth);
      dispatch(logout());
      navigate("/");
    } catch (error) {
      console.error("Logout failed:", error);
    }
  };

  const toggleFullscreen = () => {
    if (!document.fullscreenElement) {
      document.documentElement
        .requestFullscreen()
        .then(() => setIsFullscreen(true));
    } else {
      document.exitFullscreen().then(() => setIsFullscreen(false));
    }
  };

  const QuickAddMenu = () => {
    const { permissions } = useAppSelector(
      (state: RootState) => state.permissions,
    );
    const isStaffLimitReached =
      snapshot.length > 0 &&
      snapshot[0].plan_id !== "bench_unlimited" &&
      snapshot[0].remaining_staff_limit <= 0;
    const isBranchLimitReached =
      snapshot.length > 0 &&
      snapshot[0].plan_id !== "bench_unlimited" &&
      snapshot[0].remaining_branch_limit <= 0;

    return (
      <Menu
        shadow="md"
        width={160}
        position={isMobile ? "bottom" : "bottom-end"}
      >
        <Menu.Target>
          <ActionIcon color="#ee374d" size={isSmallMobile ? "sm" : "md"}>
            <IconCirclePlus stroke={1.5} size={28} />
          </ActionIcon>
        </Menu.Target>
        <Menu.Dropdown>
          {quickAddLinks.map((item) => {
            let disabled = false;
            let tooltipLabel = "";

            if (item.resource && permissions && permissions[item.resource]) {
              if (!permissions[item.resource].create) {
                disabled = true;
                tooltipLabel = `No permission to create ${item.label.toLowerCase()}`;
              }
            }

            if (!disabled && item.label === "Staff" && isStaffLimitReached) {
              disabled = true;
              tooltipLabel = "Staff limit reached";
            } else if (
              !disabled &&
              item.label === "Branch" &&
              isBranchLimitReached
            ) {
              disabled = true;
              tooltipLabel = "Branch limit reached";
            }

            const menuItem = (
              <Menu.Item
                key={item.path}
                icon={<IconPlus size={14} />}
                component={disabled ? "div" : (Link as any)}
                to={disabled ? undefined : item.path}
                onClick={
                  disabled ? (e: React.MouseEvent) => e.preventDefault() : close
                }
                disabled={disabled}
                color={disabled ? "gray" : undefined}
              >
                {item.label}
              </Menu.Item>
            );

            if (disabled) {
              return (
                <Tooltip
                  key={item.path}
                  label={tooltipLabel}
                  position="left"
                  withArrow
                >
                  <div style={{ cursor: "not-allowed" }}>{menuItem}</div>
                </Tooltip>
              );
            }

            return menuItem;
          })}
        </Menu.Dropdown>
      </Menu>
    );
  };
  const getStatusColor = (status: string) => {
    switch (status) {
      case "active":
        return "green";
      case "dnd":
        return "red";
      case "busy":
        return "orange";
      default:
        return "gray";
    }
  };

  const UserMenu = () => {
    return (
      <UnstyledButton
        className={cx(classes.user, {
          [classes.userActive]: userMenuOpened,
        })}
        style={
          {
            padding: "2px 8px 2px 4px",
            borderRadius: "100px",
            backgroundColor: "#fff",
            boxShadow: "0 1px 4px rgba(0,0,0,0.08)",
            border: "1px solid #eee",
            transition: "all 0.2s ease",
            "&:hover": {
              boxShadow: "0 2px 8px rgba(0,0,0,0.12)",
              transform: "translateY(-1px)",
            },
          } as any
        }
      >
        <Group spacing={10} position="apart" onClick={handleProfile} noWrap>
          <Box
            pos="relative"
            style={{
              display: "flex",
              alignItems: "center",
              padding: "2px",
              // border: `2px solid #ee374d`,
              borderRadius: "50%",
            }}
          >
            <Avatar
              src={user?.image}
              alt={user?.first_name}
              radius="xl"
              size={isSmallMobile ? 28 : 32}
            />
            <Box
              pos="absolute"
              bottom={0}
              right={0}
              w={10}
              h={10}
              bg={getStatusColor(status)}
              style={{
                borderRadius: "50%",
                border: "2px solid white",
              }}
            />
          </Box>
          <div style={{ display: isSmallMobile ? "none" : "block" }}>
            <Text
              className={classes.nametext}
              weight={600}
              size="sm"
              sx={{ lineHeight: 1, color: "black", whiteSpace: "nowrap" }}
            >
              {user?.first_name || user?.name} {user?.last_name}
            </Text>
          </div>
          <IconChevronDown
            size={14}
            stroke={2.5}
            color="#ee374d"
            style={{ opacity: 0.8 }}
          />
        </Group>
      </UnstyledButton>
      //       <Menu
      //         width={260}
      //         position="bottom-end"
      //         transitionProps={{ transition: "pop-top-right" }}
      //         onClose={() => setUserMenuOpened(false)}
      //         onOpen={() => setUserMenuOpened(true)}
      //         withinPortal
      //       >
      //         <Menu.Target>
      //           <UnstyledButton
      //             className={cx(classes.user, {
      //               [classes.userActive]: userMenuOpened,
      //             })}
      //           >
      //             <Group spacing={7} position="apart" onClick={handleProfile}>
      //               <Box pos="relative" >
      //                 <Avatar
      //                   src={user?.image}
      //                   alt={user?.first_name}
      //                   radius="xl"
      //                   size={isSmallMobile ? 28 : 32}
      //                 />
      //                 <Box
      //                   pos="absolute"
      //                   bottom={2}
      //                   right={2}
      //                   w={12}
      //                   h={12}
      //                   bg={getStatusColor(status)}
      //                   style={{
      //                     borderRadius: "50%",
      //                     border: "2px solid white",
      //                   }}
      //                 />
      //               </Box>
      //               <Text
      //                 className={classes.nametext}
      //                 weight={500}
      //                 size="sm"
      //                 sx={{ lineHeight: 1, color: "black" }}
      //                 mr={3}
      //               >
      //                 {user?.first_name || user?.name} {user?.last_name}
      //               </Text>
      //               <IconChevronDown size={rem(12)} stroke={1.5} />
      //             </Group>
      //           </UnstyledButton>
      //         </Menu.Target>

      //         <Menu.Dropdown>
      //           <Menu.Item
      //             icon={<IconUserCircle size="0.9rem" stroke={1.5} />}
      //             onClick={handleProfile}
      //           >
      //             My Profile
      //           </Menu.Item>
      // {/*
      //           <Menu.Item
      //             icon={<IconSettings size="0.9rem" stroke={1.5} />}
      //             onClick={handleSettingOverviewClick}
      //           >
      //             Settings
      //           </Menu.Item>

      //           <Menu.Item
      //             icon={<IconLogout size="0.9rem" stroke={1.5} />}
      //             onClick={handleLogout}
      //           >
      //             Logout
      //           </Menu.Item> */}
      //           {/* <Divider /> */}
      //           {/* Status Submenu */}

      //           {/* <Menu.Label>Change Status</Menu.Label>
      //           {statusOptions.map((status) => (
      //             <Menu.Item
      //               key={status.value}
      //               icon={<status.icon size={16} color={status.color} />}
      //               onClick={() => handleStatusChange(status.value)}
      //             >
      //               <Text color={status.color} fw={500}>
      //                 {status.label}
      //               </Text>
      //             </Menu.Item>
      //           ))} */}
      //         </Menu.Dropdown>
      //       </Menu>
    );
  };

  return (
    <>
      <Header
        height={isMobile ? HEADER_HEIGHT_MOBILE : HEADER_HEIGHT}
        mb={120}
        className={classes.header}
      >
        <Container size="lg">
          {/* Logo and Top Controls */}
          <div className={classes.inner}>
            <div className={classes.logoContainer}>
              <BenchLogo />
              <ActionIcon
                onClick={toggleFullscreen}
                size={isMobile ? "sm" : "md"}
                variant="transparent"
                color="gray"
              >
                {isFullscreen ? (
                  <IconMaximizeOff stroke={1.5} size={26} />
                ) : (
                  <IconMaximize stroke={1.5} size={26} />
                )}
              </ActionIcon>
            </div>

            <div className={classes.rightSection} style={{ gap: "20px" }}>
              {attendanceEnabled && <SimpleRemoteCheckFeature />}
              {/* Quick Add Menu */}
              <QuickAddMenu />

              {hasChat && (
                <Tooltip label="Chat" withArrow>
                  <ActionIcon
                    onClick={() => navigate(chatLink)}
                    size={isSmallMobile ? "sm" : "md"}
                    // variant="outline"
                    color="#ee374d"
                  >
                    <IconBrandTelegram stroke={1.5} size={28} />
                  </ActionIcon>
                </Tooltip>
              )}

              <Tooltip label="Settings" withArrow>
                <ActionIcon
                  onClick={handleSettingClick}
                  size={isSmallMobile ? "sm" : "md"}
                  variant="transparent"
                  color="#ee374d"
                >
                  <IconSettings stroke={1.5} size={28} />
                </ActionIcon>
              </Tooltip>

              <NotificationReceiver />
              {/* <ActionIcon onClick={()=>setChatDrawer(!chatDrawer )}>
    <IconMessageCircle color="rgb(134, 142, 150)" />
   </ ActionIcon> */}
              {/* User Menu */}
              <UserMenu />
              {/* Upgrade Button */}
              {/* Upgrade Button for Desktop */}
              {/* {!isMobile &&
                roleId === "company_admin" &&
                snapshot[0]?.plan_id === "starter" && (
                  <Button
                    variant="gradient"
                    gradient={{
                      from: "rgba(200, 6, 6, 1)",
                      to: "rgba(223, 18, 18, 1)",
                    }}
                    size="sm"
                    radius="md"
                    px="lg"
                    component={Link}
                    to={"/newsubscriptionpage"}
                    className={classes.upgradeButton}
                  >
                    Upgrade 🚀
                  </Button>
                )} */}

              {/* Mobile Menu Burger */}
              <Burger
                opened={opened}
                onClick={toggle}
                className={classes.burger}
                size="sm"
              />
            </div>
          </div>

          {/* Navigation Links for Desktop */}
          {!isMobile && (
            <div style={{ marginTop: "11px" }}>
              <NavbarList mainLinks={filteredLinks} />
            </div>
          )}

          {/* Mobile Menu Dropdown */}
          <Transition
            transition="pop-top-right"
            duration={200}
            mounted={opened}
          >
            {(styles) => (
              <Paper className={classes.dropdown} style={styles}>
                <div className={classes.mobileMenu}>
                  <div className={classes.mobileMenuSection}>
                    {/* Use the mobile-specific rendering for mobile view */}
                    {renderMobileLinks()}
                  </div>

                  {/* Mobile-only upgrade button */}
                  {/* {isMobile &&
                    roleId === "company_admin" &&
                    snapshot[0]?.plan_id === "starter" && (
                      <Button
                        variant="gradient"
                        gradient={{
                          from: "rgba(200, 6, 6, 1)",
                          to: "rgba(223, 18, 18, 1)",
                        }}
                        size="sm"
                        radius="md"
                        component={Link}
                        to={"/newsubscriptionpage"}
                        className={classes.mobileUpgradeButton}
                        onClick={close}
                      >
                        Upgrade 🚀
                      </Button>
                    )} */}
                </div>
              </Paper>
            )}
          </Transition>
        </Container>
      </Header>
      <ProfileDrawer
        opened={openedProfile}
        onClose={closeProfile}
        companyId={companyId}
        roleDetails={roleDetails}
      />
    </>
  );
};

export default MultiLayerHeader;
